import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './P2pOrders.css';

// Mock data for P2P orders
const mockOrders = [
  {
    id: '9226448442254659',
    status: 'Verifying Payment',
    statusType: 'pending',
    crypto: 'USDT',
    type: 'Buy',
    timestamp: 'Today at 1:41 PM',
    fiatAmount: '18,450.00',
    currency: 'PHP',
    price: '61.5',
    priceUnit: 'USDT/PHP',
    amount: '300.00',
    merchant: {
      name: 'POSITIVO FAST TRADER',
      verified: true
    }
  },
  {
    id: '9573006193730402',
    status: 'Completed',
    statusType: 'completed',
    crypto: 'USDT',
    type: 'Buy',
    timestamp: 'Yesterday at 3:38 PM',
    fiatAmount: '615.00',
    currency: 'PHP',
    price: '61.5',
    priceUnit: 'USDT/PHP',
    amount: '10.00',
    merchant: {
      name: 'Esther Everyday Exchange',
      verified: true
    }
  },
  {
    id: '4321098765432109',
    status: 'Cancelled',
    statusType: 'cancelled',
    crypto: 'USDT',
    type: 'Buy',
    timestamp: 'Dec 12, 2024 at 2:10 PM',
    fiatAmount: '3,075.00',
    currency: 'PHP',
    price: '61.5',
    priceUnit: 'USDT/PHP',
    amount: '50.00',
    merchant: {
      name: 'Fast Trade Solutions',
      verified: false
    }
  },
  {
    id: '9876543210987654',
    status: 'Disputed',
    statusType: 'disputed',
    crypto: 'USDT',
    type: 'Buy',
    timestamp: 'Dec 10, 2024 at 11:15 AM',
    fiatAmount: '30,750.00',
    currency: 'PHP',
    price: '61.5',
    priceUnit: 'USDT/PHP',
    amount: '500.00',
    merchant: {
      name: 'Global Crypto Trade',
      verified: false
    }
  }
];

type TabType = 'all' | 'pending' | 'completed' | 'cancelled' | 'disputed';

const P2pOrders: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('all');

  const handleBack = () => {
    navigate(-1);
  };

  const handleDisputes = () => {
    console.log('Navigate to disputes');
  };

  const handleOrderClick = (orderId: string) => {
    console.log('View order:', orderId);
  };

  const handleChatClick = (orderId: string) => {
    console.log('Open chat for order:', orderId);
  };

  const filterOrders = () => {
    if (activeTab === 'all') return mockOrders;
    return mockOrders.filter(order => order.statusType === activeTab);
  };

  const filteredOrders = filterOrders();

  return (
    <div className="p2p-orders-container">
      {/* Header */}
      <header className="p2p-orders-header">
        <button className="p2p-orders-back-button" onClick={handleBack} aria-label="Go back">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        <h1 className="p2p-orders-title">My Orders</h1>
        
        <button className="p2p-orders-disputes-button" onClick={handleDisputes}>
          Disputes
        </button>
      </header>

      {/* Tabs */}
      <div className="p2p-orders-tabs-wrapper">
        <div className="p2p-orders-tabs">
          <button 
            className={`p2p-orders-tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All
          </button>
          <button 
            className={`p2p-orders-tab ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            Pending
          </button>
          <button 
            className={`p2p-orders-tab ${activeTab === 'completed' ? 'active' : ''}`}
            onClick={() => setActiveTab('completed')}
          >
            Completed
          </button>
          <button 
            className={`p2p-orders-tab ${activeTab === 'cancelled' ? 'active' : ''}`}
            onClick={() => setActiveTab('cancelled')}
          >
            Cancelled
          </button>
          <button 
            className={`p2p-orders-tab ${activeTab === 'disputed' ? 'active' : ''}`}
            onClick={() => setActiveTab('disputed')}
          >
            Disputed
          </button>
        </div>
      </div>

      {/* Orders List */}
      <div className="p2p-orders-content">
        {filteredOrders.length === 0 ? (
          <div className="p2p-orders-empty">
            <p>No orders found</p>
          </div>
        ) : (
          filteredOrders.map((order) => (
            <div key={order.id} className="p2p-orders-card">
              {/* Status Header */}
              <div className={`p2p-orders-status-header status-${order.statusType}`}>
                <span className="p2p-orders-status-text">{order.status}</span>
              </div>

              {/* Crypto and Type */}
              <div className="p2p-orders-crypto-row">
                <span className="p2p-orders-crypto">{order.crypto}</span>
                <div className="p2p-orders-type-badge">
                  {order.type}
                </div>
                <span className="p2p-orders-timestamp">{order.timestamp}</span>
              </div>

              {/* Divider */}
              <div className="p2p-orders-divider" />

              {/* Order Details */}
              <div className="p2p-orders-details-row">
                <div className="p2p-orders-detail-group">
                  <span className="p2p-orders-detail-label">Fiat Amount</span>
                  <span className="p2p-orders-detail-amount">{order.fiatAmount}</span>
                  <span className="p2p-orders-detail-currency">{order.currency}</span>
                </div>

                <div className="p2p-orders-detail-group p2p-orders-detail-group-bordered">
                  <span className="p2p-orders-detail-label">Price</span>
                  <span className="p2p-orders-detail-amount">{order.price}</span>
                  <span className="p2p-orders-detail-currency">{order.priceUnit}</span>
                </div>

                <div className="p2p-orders-detail-group">
                  <span className="p2p-orders-detail-label">Amount</span>
                  <span className="p2p-orders-detail-amount">{order.amount}</span>
                  <span className="p2p-orders-detail-currency">{order.crypto}</span>
                </div>
              </div>

              {/* Divider */}
              <div className="p2p-orders-divider" />

              {/* Merchant Info */}
              <div className="p2p-orders-merchant-row">
                <div className="p2p-orders-merchant-info">
                  <span className="p2p-orders-merchant-name">
                    {order.merchant.name}
                    {order.merchant.verified && (
                      <svg className="p2p-orders-verified-badge" width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <circle cx="7" cy="7" r="7" fill="#22c35d"/>
                        <path d="M4 7L6 9L10 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </span>
                  <span className="p2p-orders-order-id">Order ID: {order.id}</span>
                </div>

                <button 
                  className="p2p-orders-chat-button" 
                  onClick={() => handleChatClick(order.id)}
                  aria-label="Chat"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M14 6.66667C14 9.244 11.76 11.3333 9 11.3333C8.27 11.3333 7.574 11.184 6.94 10.9107L4 12L4.894 9.06067C4.62067 8.42667 4.47133 7.73067 4.47133 7C4.47133 4.42267 6.71133 2.33333 9.47133 2.33333C12.2313 2.33333 14.4713 4.42267 14.4713 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default P2pOrders;